import { Component, Suspense, useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { easing } from 'maath';
import { AsteroidRing } from './celestial/AsteroidRing.jsx';
import { Moon } from './celestial/Moon.jsx';
import { Planet } from './celestial/Planet.jsx';
import { RingedPlanet } from './celestial/RingedPlanet.jsx';
import { SolarSystem } from './celestial/SolarSystem.jsx';
import { Star } from './celestial/Star.jsx';
import { sectionViewportOffset, trackSections } from '../scroll/sectionRegistry.js';

const SECTION_IDS = ['hero', 'who', 'work', 'experience', 'credentials', 'contact'];
const FOV_TAN = Math.tan((45 / 2) * (Math.PI / 180));

// Degrade never breaks: a celestial whose texture fails to load renders
// nothing instead of killing the canvas.
export class CelestialBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.warn('[celestial] dropped after load failure:', error?.message ?? error);
  }

  render() {
    if (this.state.failed) return null;
    return <Suspense fallback={null}>{this.props.children}</Suspense>;
  }
}

// Anchors a celestial group beside its DOM section: world Y tracks the
// section's viewport position (delta-time damped), so the object rides with
// its tile — the lunar-object motif.
function Anchored({ id, x = 0, z = -2, parallax = 0.9, baseY = 0, children }) {
  const ref = useRef();
  const { camera } = useThree();

  useFrame((_, dt) => {
    if (!ref.current) return;
    const offset = sectionViewportOffset(id);
    const worldH = 2 * (camera.position.z - z) * FOV_TAN;
    const targetY = baseY + offset * worldH * parallax;
    easing.damp3(ref.current.position, [x, targetY, z], 0.16, dt);
    const visible = Math.abs(offset) < 1.6;
    if (ref.current.visible !== visible) ref.current.visible = visible;
  });

  return (
    <group ref={ref} position={[x, baseY - 4, z]}>
      {children}
    </group>
  );
}

export function FlatScene() {
  useEffect(() => trackSections(SECTION_IDS), []);

  return (
    <>
      {/* hero — the Grok video (DOM layer) owns the hero backdrop now */}
      {/* about — planet + companion moon beside the tile (lunar motif) */}
      <Anchored id="who" x={4.1} z={-1.8} parallax={0.8}>
        <CelestialBoundary>
          <Planet map="/assets/textures/jupiter_2k.jpg" radius={0.9} atmosphereColor="#d8b28a" atmosphereIntensity={0.45} spin={0.06} />
          <Moon position={[-1.35, 0.55, 0.4]} radius={0.16} />
        </CelestialBoundary>
      </Anchored>
      {/* case studies — the Grok work video (DOM layer) owns this backdrop now */}
      {/* experience — the star */}
      <Anchored id="experience" x={-3.1} z={-2} parallax={0.85}>
        <Star position={[0, 0, 0]} />
      </Anchored>
      {/* credentials — ringed Neptune beside the headline, above the panel */}
      <Anchored id="credentials" x={4.0} z={-2.4} parallax={0.8} baseY={2.1}>
        <CelestialBoundary>
          <RingedPlanet radius={0.85} />
          <AsteroidRing />
        </CelestialBoundary>
      </Anchored>
      {/* contact — the whole solar system, slow and correct, behind the panel */}
      <Anchored id="contact" x={0} z={-2.6} parallax={0.4} baseY={-0.2}>
        <CelestialBoundary>
          <SolarSystem position={[0, 0, 0]} />
        </CelestialBoundary>
      </Anchored>
    </>
  );
}
